import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectOverview } from '../../../util/store/selectors';
import { aWeekMillisecond } from '../../../util/varibles/constants';
import { getKeyWeek } from '../../../util/varibles/global';
import { CALENDAR_ID } from '../constants';
import { overviewActions } from '../reducer';
import styles from './style.module.scss';

interface IProps {
    calendarWidth: number
}

const LazyLoading: React.FC<IProps> = ({ calendarWidth }) => {
    const dispatch = useDispatch();
    const weeks = useSelector(selectOverview.weeks);
    const loadingLazy = useSelector(selectOverview.loadingLazy);
    const beforeCount = useSelector(selectOverview.beforeCount);
    const afterCount = useSelector(selectOverview.afterCount);
    const wellboats = useSelector(selectOverview.wellboats);
    const [loadMore, setLoadMore] = useState<0 | 1 | 2>(0);
    const previousWeeksLength = useRef(weeks.length);
    const scrollAnchor = useRef<{ dayId: string, offset: number } | null>(null);

    useLayoutEffect(() => {
        const anchor = scrollAnchor.current;
        if (!anchor)
            return;

        const calendar = document.getElementById(CALENDAR_ID);
        const dayElement = document.getElementById(anchor.dayId);
        if (calendar && dayElement) {
            const offset = dayElement.getBoundingClientRect().top - calendar.getBoundingClientRect().top;
            calendar.scrollTop += offset - anchor.offset;
            scrollAnchor.current = null;
        }
    }, [weeks]);

    useEffect(() => {
        if (weeks.length !== previousWeeksLength.current) {
            previousWeeksLength.current = weeks.length;
            setLoadMore(0);
        }
        if (!loadingLazy)
            setLoadMore(0);
    }, [loadingLazy, weeks.length]);

    useEffect(() => {
        const calendar = document.getElementById(CALENDAR_ID);
        if (!calendar || weeks.length === 0)
            return;

        const handleWheel = (event: WheelEvent) => {
            if (loadMore || loadingLazy)
                return;

            const { deltaY } = event;
            const isAtTop = calendar.scrollTop + deltaY <= 0;
            const isAtBottom = calendar.scrollHeight - calendar.clientHeight - (calendar.scrollTop + deltaY) < 0;

            if (isAtTop && deltaY < 0) {
                const firstTime = weeks[0].time;
                const dayElement = document.getElementById(`${firstTime}`);
                if (dayElement) {
                    scrollAnchor.current = {
                        dayId: `${firstTime}`,
                        offset: dayElement.getBoundingClientRect().top - calendar.getBoundingClientRect().top,
                    };
                }

                const newWeeks = Array.from({ length: 3 }, (_, index) => {
                    const time = firstTime - ((3 - index) * aWeekMillisecond);
                    return { key: getKeyWeek(time), time };
                });
                setLoadMore(1);
                dispatch(overviewActions.lazyLoadingRequest({
                    isLoad: beforeCount || wellboats.before_count,
                    key: 'beforeCount',
                    weeks: [...newWeeks, ...weeks],
                    startTime: firstTime - (3 * aWeekMillisecond),
                    finishTime: firstTime - 1,
                }));
            } else if (isAtBottom && deltaY > 0) {
                const lastTime = weeks[weeks.length - 1].time;
                const newWeeks = Array.from({ length: 3 }, (_, index) => {
                    const time = lastTime + ((index + 1) * aWeekMillisecond);
                    return { key: getKeyWeek(time), time };
                });
                setLoadMore(2);
                dispatch(overviewActions.lazyLoadingRequest({
                    isLoad: afterCount || wellboats.after_count,
                    key: 'afterCount',
                    weeks: [...weeks, ...newWeeks],
                    startTime: lastTime + aWeekMillisecond,
                    finishTime: lastTime + (4 * aWeekMillisecond) - 1,
                }));
            }
        };

        calendar.addEventListener('wheel', handleWheel);
        return () => calendar.removeEventListener('wheel', handleWheel);
    }, [afterCount, beforeCount, dispatch, loadingLazy, loadMore, weeks, wellboats.after_count, wellboats.before_count]);

    const style = { width: `${calendarWidth}%` };

    return <>
        <div className={styles['loading-top']} data-is-show={loadMore === 1 && loadingLazy} style={style}>
            <div className={styles['container-loading']}>
                <div className="spinner-border text-secondary" role="status" />
            </div>
        </div>
        <div className={styles['loading-bottom']} data-is-show={loadMore === 2 && loadingLazy} style={style}>
            <div className={styles['container-loading']}>
                <div className="spinner-border text-secondary" role="status" />
            </div>
        </div>
    </>;
};

export default LazyLoading;
